import type { ReactNode } from 'react';

interface SimpleConfirmDialogProps {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  submitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Lightweight confirmation modal — no text input, just a confirm/cancel pair.
 * Used by flows that do NOT collect a reason (e.g. resend-invitation), in
 * contrast with `ReasonModal` which asks for justification (reset-password,
 * reset-mfa, revoke).
 */
export function SimpleConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  submitting = false,
  onConfirm,
  onCancel,
}: SimpleConfirmDialogProps) {
  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="simple-confirm-title"
        style={{
          background: 'var(--color-surface)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 420,
          width: '90%',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h2 id="simple-confirm-title" style={{ marginTop: 0, fontSize: 18 }}>
          {title}
        </h2>
        <div style={{ color: 'var(--color-text-secondary)', marginBottom: 8 }}>{body}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'btn btn-danger' : 'btn'}
            disabled={submitting}
            onClick={onConfirm}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
            }}
          >
            {submitting ? `${confirmLabel}…` : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
